import PropTypes from 'prop-types';
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
// @mui
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import Stack from '@mui/material/Stack';
import Avatar from '@mui/material/Avatar';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Badge from '@mui/material/Badge';
import IconButton from '@mui/material/IconButton';
import LinearProgress from '@mui/material/LinearProgress';
import { alpha } from '@mui/material/styles';
// hooks
import { useAuth } from 'src/hooks/useAuth';
// components
import Iconify from 'src/components/iconify';

// ----------------------------------------------------------------------

export default function SidebarPlanCard() {
    const { user } = useAuth();
    const navigate = useNavigate();
    const [isVisible, setIsVisible] = useState(false);
    const [isDismissed, setIsDismissed] = useState(false);

    // Delay appearance by 5 seconds after mount
    useEffect(() => {
        const timer = setTimeout(() => setIsVisible(true), 2000);
        return () => clearTimeout(timer);
    }, []);

    // Only show for organization admin users (not super admin)
    const userRole = user?.role || '';
    const isOrgAdmin = userRole.toLowerCase() === 'admin';

    // Check organization feature flag
    const showPlanValidity = user?.organizationFeatures?.showPlanValidity !== false;

    // Don't render if not organization admin, feature disabled, or user dismissed it
    if (!isOrgAdmin || !showPlanValidity || isDismissed) return null;

    // Get user plan info from auth context (organization plan from backend)
    const userName = user?.name || user?.firstName || 'Admin User';
    const userEmail = user?.email || 'admin@example.com';
    const userAvatar = user?.avatar || user?.photoURL || '';
    const currentPlan = user?.organizationPlan || 'Free';
    const planStatus = user?.planStatus || 'active';

    // Get plan expiry date from backend
    const planExpiryDate = user?.planExpiryDate;

    // Calculate days remaining
    const calculateDaysRemaining = () => {
        if (!planExpiryDate) return null;

        const expiry = new Date(planExpiryDate);
        const today = new Date();
        const diffTime = expiry - today;
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

        return diffDays > 0 ? diffDays : 0;
    };

    const daysRemaining = calculateDaysRemaining();
    const totalPlanDays = 30; // Default 30 days, can be dynamic
    const daysProgress = daysRemaining ? ((totalPlanDays - daysRemaining) / totalPlanDays) * 100 : 0;

    // Determine if user should see upgrade button
    const isFreePlan = currentPlan.toLowerCase() === 'free';
    const isBasicPlan = currentPlan.toLowerCase() === 'basic';
    const shouldShowUpgrade = isFreePlan || isBasicPlan;

    // Get plan badge color
    const getPlanColor = (plan) => {
        const planLower = plan.toLowerCase();
        if (planLower === 'free' || planLower === 'basic') return 'default';
        if (planLower === 'pro' || planLower === 'professional') return 'primary';
        if (planLower === 'enterprise' || planLower === 'premium') return 'success';
        return 'info';
    };

    // Get progress bar color based on days remaining
    const getProgressColor = (days) => {
        if (days < 7) return 'error.main';
        if (days < 15) return 'warning.main';
        return 'success.main';
    };

    const handleUpgradeClick = () => {
        navigate('/dashboard/billing/plans');
    };

    return (
        <Card
            sx={{
                p: 2.5,
                m: 2,
                mb: 3,
                bgcolor: (theme) => alpha(theme.palette.grey[500], 0.04),
                border: (theme) => `dashed 1px ${theme.palette.divider}`,
                borderRadius: 2,
                position: 'relative',
                opacity: isVisible ? 1 : 0,
                transform: isVisible ? 'translateY(0)' : 'translateY(12px)',
                transition: 'opacity 0.6s ease, transform 0.6s ease',
            }}
        >
            {/* Close Button */}
            <IconButton
                size="small"
                onClick={() => setIsDismissed(true)}
                sx={{
                    position: 'absolute',
                    top: 8,
                    right: 8,
                    color: 'text.secondary',
                    '&:hover': {
                        color: 'error.main',
                        bgcolor: (theme) => alpha(theme.palette.error.main, 0.08),
                    },
                }}
            >
                <Iconify icon="mingcute:close-line" width={18} />
            </IconButton>

            <Stack spacing={2.5}>
                {/* User Avatar with Plan Badge */}
                <Stack alignItems="center" spacing={1.5}>
                    <Badge
                        overlap="circular"
                        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                        badgeContent={
                            <Chip
                                label={currentPlan}
                                color={getPlanColor(currentPlan)}
                                size="small"
                                sx={{
                                    height: 24,
                                    fontWeight: 700,
                                    fontSize: '0.7rem',
                                    boxShadow: (theme) => theme.customShadows.z8,
                                }}
                            />
                        }
                    >
                        <Avatar
                            src={userAvatar}
                            alt={userName}
                            sx={{
                                width: 72,
                                height: 72,
                                border: (theme) => `solid 3px ${theme.palette.background.paper}`,
                                boxShadow: (theme) => theme.customShadows.z8,
                            }}
                        >
                            {userName.charAt(0).toUpperCase()}
                        </Avatar>
                    </Badge>

                    {/* User Name */}
                    <Box sx={{ textAlign: 'center', width: '100%' }}>
                        <Typography variant="subtitle1" fontWeight={600} noWrap>
                            {userName}
                        </Typography>
                        <Typography variant="caption" sx={{ color: 'text.secondary' }} noWrap>
                            {userEmail}
                        </Typography>
                    </Box>
                </Stack>

                {/* Plan Days Remaining */}
                {daysRemaining !== null && planStatus === 'active' && !isFreePlan && (
                    <Box>
                        <Stack direction="row" justifyContent="space-between" alignItems="center" mb={0.5}>
                            <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600 }}>
                                Plan Validity
                            </Typography>
                            <Typography variant="caption" sx={{ color: 'primary.main', fontWeight: 700 }}>
                                {daysRemaining} days left
                            </Typography>
                        </Stack>
                        <LinearProgress
                            variant="determinate"
                            value={daysProgress}
                            sx={{
                                height: 6,
                                borderRadius: 1,
                                bgcolor: (theme) => alpha(theme.palette.grey[500], 0.12),
                                '& .MuiLinearProgress-bar': {
                                    borderRadius: 1,
                                    bgcolor: getProgressColor(daysRemaining),
                                },
                            }}
                        />
                    </Box>
                )}

                {/* Upgrade Button Section */}
                {shouldShowUpgrade && planStatus === 'active' && (
                    <Button
                        fullWidth
                        variant="contained"
                        color="primary"
                        size="medium"
                        startIcon={<Iconify icon="solar:rocket-2-bold-duotone" width={20} />}
                        onClick={handleUpgradeClick}
                        sx={{
                            py: 1.25,
                            fontWeight: 700,
                            background: (theme) =>
                                `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                            boxShadow: (theme) => theme.customShadows.primary,
                            '&:hover': {
                                background: (theme) =>
                                    `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 100%)`,
                                boxShadow: (theme) => theme.customShadows.primary,
                                transform: 'translateY(-2px)',
                            },
                            transition: 'all 0.3s ease',
                        }}
                    >
                        Upgrade to Pro
                    </Button>
                )}

                {/* Pro Active Status */}
                {!shouldShowUpgrade && planStatus === 'active' && (
                    <Stack
                        direction="row"
                        spacing={1}
                        alignItems="center"
                        justifyContent="center"
                        sx={{
                            py: 1.25,
                            px: 2,
                            borderRadius: 1.5,
                            bgcolor: (theme) => alpha(theme.palette.success.main, 0.12),
                            border: (theme) => `solid 2px ${alpha(theme.palette.success.main, 0.24)}`,
                        }}
                    >
                        <Iconify icon="solar:check-circle-bold-duotone" width={20} sx={{ color: 'success.main' }} />
                        <Typography variant="body2" sx={{ color: 'success.main', fontWeight: 700 }}>
                            {currentPlan} Plan Active
                        </Typography>
                    </Stack>
                )}

                {/* Inactive Plan Warning */}
                {planStatus !== 'active' && (
                    <Stack
                        direction="row"
                        spacing={1}
                        alignItems="center"
                        justifyContent="center"
                        sx={{
                            py: 1.25,
                            px: 2,
                            borderRadius: 1.5,
                            bgcolor: (theme) => alpha(theme.palette.error.main, 0.12),
                            border: (theme) => `solid 2px ${alpha(theme.palette.error.main, 0.24)}`,
                        }}
                    >
                        <Iconify icon="solar:danger-triangle-bold-duotone" width={20} sx={{ color: 'error.main' }} />
                        <Typography variant="body2" sx={{ color: 'error.main', fontWeight: 700 }}>
                            Plan {planStatus}
                        </Typography>
                    </Stack>
                )}
            </Stack>
        </Card>
    );
}

SidebarPlanCard.propTypes = {};

